import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrintOrder } from "../../hooks/use-print-order";
import { PRINT_ITEM_STATUS, PrintDataStructure, PrintItemDataEntity, PrintProductImageDataEntity } from "../../../../../models/sift/json/print-model";
import { useParams } from "react-router-dom";
import { PathParams, RoutingPath } from "../../../../../routes/routing-path";
import { cloneDeep } from "lodash";
import { useQuery } from "../../../../../hooks/use-query";
import {
  ApiImagesCopyPost,
  ApiImagesDelete,
  ApiImagesGet,
  ApiImagesGetOne,
  ApiImagesPost,
  ImagesDeleteRequest,
  ImagesGetResponse,
  ImagesPostRequest,
} from '../../../../../api/front/images/api-images';
import {
  ApiPrintImagePost,
  ApiPrintImagesCopyPost,
  ApiPrintImagesDelete,
  PrintImagePostRequest,
  PrintImagesDataEntity,
} from '../../../../../api/sift/front/api-print-image';
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { push, replace } from "connected-react-router";
import { dialogActions } from "../../../../dialog/slice/dialog-slice";
import { GetJsonListData, printActions } from "../../../../../slices/sift/print-slice";
import { PrintSizeChange } from "../module/print-size-change";
import { useLoading } from "../../../../ui/loading/hooks/use-loading";
import { TrimUiManager } from "../../../../../trim-editor/manager/ui/trim.ui.manager";
import { TrimEditableImage } from "../../../../../trim-editor/manager/image-edit/trim.editable-image";
import { usePrintImages } from "../../hooks/use-print-images";
import { printSelectImageSort } from "../../../../../models/sift/select/print-select-images.model";
import { setImage } from "../../../../../slices/orders/use-image/use-image";
import { commonActions } from "../../../../../slices/common-slice";
import { usePrintLock } from "../../hooks/use-print-lock";
import { usePrintStatus } from "../../hooks/use-print-status";
import { DateFormatter } from "../../../../../utilities/date-formatter";
import { apiActions } from "../../../../../slices/api-slice";
import { IMAGES_KIND } from "../../../../../layout-editor/manager/image-processor/image-processor.manager";
import { UuidGenerator } from "../../../../../utilities/uuid-generator";
import { ApiPrintShopOrderDelete } from "../../../../../api/sift/front/api-print-meta-shop-order-data";
import { SeriousErrorDialog } from "../../../../dialog/serious-error-dialog";
import { DATE_TEXT_ID } from '../../../../../trim-editor/model/trim.print-model';
import { NewPage } from '../../../../../utilities/new-page';
import { webPagePath } from '../../../../../models/web-page-path';
import { usePrintUploadFiles } from '../../hooks/use-print-upload-files';

export const PRINT_SELECT_IMAGES_VALIDATION = {
  min: 0,
  max: 9999,
} as const;

export const KIND_QUERY = {
  image: 'i',
  trimming: 't',
  date: 'd',
} as const;

export type ThumbImagesDataEntity = { imageId: string, thumbId?: string, image?: string };

export type PrintSelectImageData = Omit<Partial<PrintProductImageDataEntity>, 'id'> & PrintImagesDataEntity & { editableImage?: TrimEditableImage, thumbBase64?: string };
type Kind = 'image' | 'trimming' | 'date';
export const usePrintSelectImages = () => {
  const { kijshopCd, shopOrderId } = useParams<PathParams>();
  const lockActions = usePrintLock();
  const dispatch = useAppDispatch();
  const {
    shopMeta,
    machineId,
    jsonPath,
  } = useAppSelector((state) => ({
    shopMeta: state.systemConfig.metaData,
    machineId: state.storage.machineId,
    jsonPath: state.print.filePath,
  }))
  const loading = useLoading({});
  const {
    getData,
    getMetaData,
    getImages,
  } = usePrintOrder();
  const {
    changeStatus,
    changeLastUpdateDate,
  } = usePrintStatus();
  const { filterUploadFiles } = usePrintUploadFiles();

  const query = useQuery();
  const [structure, setStructure] = useState(PrintDataStructure.create(kijshopCd, shopOrderId));
  const [datas, setDatas] = useState<GetJsonListData>([]);
  const [selectMode, setSelectMode] = useState<Kind>('image');
  const [isInitializing, setIsInitializing] = useState(false);
  const [sortValue, setSortValue] = useState('name_ask');
  const [viewLarge, setViewLarge] = useState(false);
  const [resetThumbList, setResetThumbList] = useState<string[]>([]);
  const [uploadImageData, setUploadImageData] = useState<PrintImagesDataEntity[]>([]);
  const [saveThumbImages, setSaveThumbImages] = useState<ThumbImagesDataEntity[]>([]);
  const {
    images,
    getEditableImages,
    setImages,
    imageUpload,
    createThumbImage,
    thumbImages,
    getThumbImages,
    uploadProgress,
  } = usePrintImages({ structure, ignoreInitGetThumbImages: true });

  const leavePage = useRef<boolean>(false);
  const viewImages = useMemo(() => {
    const clone = cloneDeep(images);
    clone.forEach((v) => {
      if (v.thumbId && resetThumbList.includes(v.id)) {
        v.thumbId = undefined;
      }
    })
    const target = printSelectImageSort.find((v) => v.value === sortValue);
    if (target) {
      target.sort(clone);
    }
    return clone;
  }, [
    images,
    sortValue,
    resetThumbList,
  ]);

  // フッターState
  const footerState = useState(true);

  const themeClassName = useMemo(() => {
    return selectMode === 'image' ? '' : `theme_${selectMode}`;
  }, [selectMode]);
  const selectItem = useMemo(() => {
    const id = query.get('t') ?? '';
    const target = structure?.item?.[id];
    return target;
  }, [structure, query]);

  const getRotate = useCallback((image: PrintSelectImageData) => {
    if (!selectItem) return 'horizontal';
    const direction = !Number(image.rotate) ? '1' : '2';
    const printDateFlg = !!image.datePrint;
    const tmp = structure.getTemplate(selectItem?.id, direction, printDateFlg);
    const base = tmp.template.layout.print?.[0].base?.[0].$;
    return Number(base.width) >  Number(base.height) ? 'horizontal' : 'vertical';
  }, [
    structure,
    selectItem,
  ]);

  const uploadThumb = useCallback((request: PrintImagePostRequest & { id: string }) => {
    return new Promise<void>((resolve) => {
      dispatch(apiActions.run(
        new ApiPrintImagePost(request),
        {
          onSuccess: () => resolve(),
        },
      ))
    })
  }, [
    dispatch,
  ]);

  const deleteThumb = useCallback((request: ImagesDeleteRequest) => {
    return new Promise<void>((resolve) => {
      dispatch(apiActions.run(
        new ApiPrintImagesDelete(request),
        {
          onSuccess: () => resolve(),
        },
      ))
    });
  }, [
    dispatch,
  ]);

  const save = useCallback(async(onSuccess: () => void, isComplete?: boolean) => {
    if (!selectItem) {
      throw new Error('select item is not found !!');
    }
    if (isComplete) {
      const editableImageList = images.map((v) => v.editableImage);
      const items = structure.getItemList();
      const imagesData = items.map((v) => {
        if (v.id === selectItem.id) {
          return images;
        }
        return v.images;
      });
      if (
        imagesData.flat().find((v) => {
          return v.count && !editableImageList.find((ei) => ei?.id === v.id);
        })
      ) {
        dispatch(dialogActions.pushMessage({
          title: '確認',
          message: [
            '画像の生成が完了していません。',
            '恐れ入りますがしばらく待ってから再度お試しください。',
          ],
          buttons: [
            { label: 'OK', callback: () => dispatch(dialogActions.pop()) },
          ],
        }));
        return;
      }
    }
    selectItem.images = images.map((v) => ({
      id: v.id,
      count: v.count ?? 0,
      trimmingStatus: v.trimmingStatus ?? 1,
      datePrint: v.datePrint ?? false,
      rotate: v.rotate ?? '0',
      thumbId: v.thumbId,
    }));
    const useImages = structure.getUseImageIdList();
    structure.toneCorrection = structure.toneCorrection.filter((v) => {
      return useImages.find((v2) => v.id === v2);
    });
    if (
      !selectItem.images.find((v) => v.trimmingStatus === 3 && v.count)
      && !structure.toneCorrection
        .find((v) => v.value && selectItem.images.find((v2) => v2.count && v.id === v2.id))
    ) {
      selectItem.memo = undefined;
    }
    useImages.forEach((v) => {
      if (!structure.toneCorrection.find((v2) => v === v2.id)) {
        structure.toneCorrection.push({
          id: v,
          memo: '',
          value: !!shopMeta.printSetting?.toneCorrection,
        })
      }
    });
    for (const item of structure.getItemList()) {
      if (isComplete) {
        const invalid = structure.getInvalidCompleteItems(selectItem.id, images);
        if (!item.images.some((v) => v.count)) {
          dispatch(dialogActions.pushMessage({
            title: '警告',
            message: [
              'プリントする写真を選択してください',
              ...invalid.map((v) => `・${v.name}`),
            ],
            buttons: [
              { label: 'OK', callback: () => dispatch(dialogActions.pop()) },
            ],
          }));
          return;
        } else {
          item.status = PRINT_ITEM_STATUS.enableOrder;
        }
      } else {
        // ①すべての商品のステータスを「注文作成済」に戻す
        item.status = PRINT_ITEM_STATUS.created;

        // ②ユーザーが一時保存を実行した際に開いていた商品だけ「注文作成済」に戻す
        // if (!item.images.some((v) => v.count)) {
        //   item.status = PRINT_ITEM_STATUS.created;
        // }
        // selectItem.status = PRINT_ITEM_STATUS.created;
      }
    };
    const processData = {
      date: DateFormatter.date2str(new Date(), 'YYYYMMDD_HHmmSS', '/'),
      machineId,
    }
    selectItem.process = selectItem.process
      ? { ...selectItem.process, finish: processData }
      : { start: processData, finish: processData };
    const updateThumbIdList: { id: string, data?: string, imgData: PrintProductImageDataEntity }[] = [];
    // selectItem.images.forEach((v) => {
    loading.setLoadingMessage('保存処理中です...');
    for (const imgData of selectItem.images) {
      const targetImageData = images.find((img) => img.id === imgData.id);
      const target = resetThumbList.find((targetId) => targetId === imgData.id);
      if (target && imgData.thumbId && !isComplete){
      // if (target && imgData.thumbId && !isComplete){
        updateThumbIdList.push({ id: imgData.thumbId, data: targetImageData?.thumbBase64, imgData });
      }
      if (target && !isComplete) {
        imgData.thumbId = undefined;
      }
      if (isComplete && imgData.thumbId && !imgData.count) {
        imgData.thumbId = undefined;
      }

      // if ((isComplete && imgData.count) && (!imgData.thumbId || target)) {
      if ((isComplete && imgData.count) && imgData.trimmingStatus !== 2) {
        const uuid = (() => {
          if (imgData.thumbId) {
            return imgData.thumbId;
          } else {
            return UuidGenerator.create();
          }
        })();
        imgData.thumbId = uuid;
        let thumbData: string | undefined = targetImageData?.thumbBase64;
        // if (!thumbData) {
          thumbData = await createThumbImage(
            imgData.id,
            selectItem,
            undefined,
            undefined,
            true,
            undefined,
            true
          );
        // }
        updateThumbIdList.push({ id: uuid, data: thumbData, imgData });
      }
    };
    if (isComplete) {
      const list = structure.getItemList().filter((v) => v.id !== selectItem.id);
      for (const item of list) {
        const itemImageList = item.images.filter((v) => images.find((v2) => v2.id === v.id)).map((v) => {
          const target = images.find((v2) => v.id === v2.id)!;
          return {
            ...target,
            ...v,
            id: target.id,
            thumbBase64: undefined,
          }
        });
        for (const img of item.images) {
          if (!img.count) {
            img.thumbId = undefined;
            continue;
          }
          if (!img.thumbId) {
            const thumb = await createThumbImage(
              img.id,
              item,
              itemImageList,
              undefined,
              true,
            );
            const id = UuidGenerator.create();
            img.thumbId = id;
            updateThumbIdList.push({ id, data: thumb, imgData: img });
          }
        }
      }
    }
    await lockActions.check();
    dispatch(printActions.shopOrder({ kijshopCd, shopOrderId }).update(structure, {
      success: async() => {
        for (const resetData of resetThumbList) {
          const target = datas.find((v) => v.filename === `${selectItem?.id ?? ''}_${resetData}`);
          if (target) {
            await new Promise<void>((resolve, reject) => {
              dispatch(apiActions.run(
                new ApiPrintShopOrderDelete({
                  kijshopCd, shopOrderId,
                  path: [`${jsonPath}/${target.filename}`]
                }),
                {
                  onSuccess: () => resolve(),
                  onError: () => reject(),
                }
              ))
            })
          }
        }
        if (isComplete) {
          const list = updateThumbIdList.filter((v) => v.data);
          if (list.length) {
            for (const uploadImgData of list) {
              const file = (() => {
                const [metadata, data] = uploadImgData.data!.split(',');
                const mime = metadata.match(/data:(.*);base64/)?.[1] || 'application/octet-stream';
                const binary = atob(data);
                const len = binary.length;
                const bytes = new Uint8Array(len);

                for (let i = 0; i < len; i++) {
                  bytes[i] = binary.charCodeAt(i);
                }

                const blob = new Blob([bytes], { type: mime });
                return new File([blob], `${uploadImgData.id}.jpg`, { type: mime });
              })();
              await uploadThumb({
                id: uploadImgData.id,
                data: file,
                kijshopCd,
                shopOrderId,
                kind: IMAGES_KIND.TMP_THUMB,
                filename: file.name,
                exifCreated: '',
              });
            }
          }
          await changeStatus('enableOrder');
        } else {
          if (updateThumbIdList.length) {
            await Promise.all(updateThumbIdList.map((v) => deleteThumb({
              kind: IMAGES_KIND.TMP_THUMB,
              kijshopCd,
              shopOrderId,
              filename: `${v}.jpg`,
            })));
          }
          if (!structure.getItemList().every((v) => v.status === PRINT_ITEM_STATUS.enableOrder)) {
            await changeStatus('created');
          } else {
            changeLastUpdateDate();
          }
        }
        const moveImageItems = structure.getItemList().map(v => {
          return v.images.filter(v => v.trimmingStatus === 2);
        })
        const moveImages = moveImageItems?.flat();
        for (const moveImage of moveImages) {
          const srcFilename = `${moveImage.datePrint ? (moveImage.thumbId + DATE_TEXT_ID) : moveImage.thumbId}.jpg`;
          const dstFilename = `${moveImage.thumbId}.jpg`;
          await new Promise<void>((resolve) => {
            dispatch(apiActions.run(
              new ApiPrintImagesCopyPost({
                kijshopCd,
                shopOrderId,
                srcKind: IMAGES_KIND.THUMB_PREVIEW,
                dstKind: IMAGES_KIND.TMP_THUMB,
                srcFilename,
                dstFilename,
              }),
              {
                onSuccess: (res) => {
                  console.log('success: ', res);
                  resolve();
                },
                onError: (e) => {
                  console.log('error: ', e);
                  resolve();
                },
              },
              { ignoreSystemError: true },
            ));
          })
        }
        // await Promise.all(moveImages.map(v => {
        //   const srcFilename = `${v.datePrint ? (v.thumbId + DATE_TEXT_ID) : v.thumbId}.jpg`;
        //   const dstFilename = `${v.thumbId}.jpg`;
        //   return new Promise<void>((resolve) => {
        //     dispatch(apiActions.run(
        //       new ApiPrintImagesCopyPost({
        //         kijshopCd,
        //         shopOrderId,
        //         srcKind: IMAGES_KIND.THUMB_PREVIEW,
        //         dstKind: IMAGES_KIND.TMP_THUMB,
        //         srcFilename,
        //         dstFilename,
        //       }),
        //       {
        //         onSuccess: (res) => {
        //           console.log('success: ', res);
        //           resolve();
        //         },
        //         onError: (e) => {
        //           console.log('error: ', e);
        //           resolve();
        //         },
        //       },
        //       { ignoreSystemError: true },
        //     ));
        //   })
        // }))
        onSuccess();
        loading.setLoadingMessage('');
      },
    }));
  }, [
    dispatch,
    changeStatus,
    kijshopCd, shopOrderId,
    structure,
    selectItem,
    images,
    loading,
    shopMeta,
    lockActions,
    uploadThumb,
    machineId,
    resetThumbList,
    deleteThumb,
    changeLastUpdateDate,
    createThumbImage,
    datas,
    jsonPath,
  ]);

  const updateStructure = useCallback((
    callback: (model: PrintDataStructure) => void,
  ) => {
    const clone = cloneDeep(structure)
    callback(clone);
    setStructure(clone);
  }, [structure]);
  const onSelectImageImageMode = useCallback((id: string) => {
    const target = images.find((v) => v.id === id);
    if (!target || target.count) return;
    target.count = 1;
    setImages(cloneDeep(images));
  }, [images]);

  const onSelectDateMode = useCallback((id) => {
    const target = images.find((v) => v.id === id);
    if (!target) return;
    target.datePrint = !target.datePrint;
    if (!target.count)  target.count = 1;
    setImages(cloneDeep(images));
  }, [images]);

  const onSelectAutoTrimmingMode = useCallback((id: string) => {
    const target = images.find((v) => v.id === id);
    const mainFnc = () => {
      if (!target) return;
      target.trimmingStatus = target.trimmingStatus !== 3 ? 3 : 1;
      if (!target.count)  target.count = 1;
      setImages(cloneDeep(images));
    }
    if (target?.trimmingStatus === 2) {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: ['トリミング編集画面での操作が破棄されます。実行してよろしいですか？'],
        buttons: [
          { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
          { label: 'OK', callback: () =>{
            const target = images.find((v) => v.id === id);
            if (target) {
              target.trimmingStatus = target.trimmingStatus !== 3 ? 3: 1;
              if (!target.count)  target.count = 1;
              target.thumbId = undefined;
            }
            setImages(cloneDeep(images));
            dispatch(dialogActions.pop());
          }},
        ]
      }));
    } else {
      mainFnc();
    }
  }, [
    setImages,
    dispatch,
    images,
  ]);
  const onSelectImage = useCallback((id: string) => {
    switch(selectMode) {
      case 'image': {
        onSelectImageImageMode(id);
        break;
      }
      case 'date': {
        onSelectDateMode(id);
        break;
      }
      case 'trimming': {
        onSelectAutoTrimmingMode(id);
        break;
      }
    }
  }, [
    selectMode,
    onSelectImageImageMode,
    onSelectDateMode,
    onSelectAutoTrimmingMode,
  ]);
  const setSelectImageCount = useCallback((id: string, count: number) => {
    const target = images.find((v) => v.id === id);
    if (!target) return;
    setImages((prev) => {
      const target = prev.find((v) => v.id === id);
      if (target) {
        target.count = count
      };
      return [...prev];
    })
  }, [images, setImages]);

  const handlerAllSelect = useCallback(async() => {
    switch(selectMode) {
      case 'image': {
        setImages((prev) => {
          const result = prev.map((v) => {
            if (!v.count) v.count = 1;
            return v;
          });
          return [...result];
        });
        break;
      } case 'trimming': {
        const clone = cloneDeep(images);
        if (clone.find((v) => v.trimmingStatus === 2)) {
          await new Promise<void>((resolve) => {
              dispatch(dialogActions.pushMessage({
                title: '確認',
                message: ['トリミング編集画面での操作が破棄されます。実行してよろしいですか？'],
                buttons: [
                  { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
                  { label: 'OK', callback: () => {
                    dispatch(dialogActions.pop());
                    resolve();
                  }},
                ]
              }));
          });
        }
        setImages(clone.map((v) => ({ ...v, trimmingStatus: 3, thumbId: undefined })));

        break;
      } case 'date': {
        setImages((prev) => {
          const result = prev.map((v) => {
            v.datePrint = true;
            return v;
          });
          return [...result];
        });
        break;
      }
    }
  }, [
    dispatch,
    images,
    setImages,
    selectMode,
  ]);

  const handlerIncrementAll = useCallback(() => {
    const data = cloneDeep(images);
    data.forEach((v) => {
      const count = (v.count ?? 0) + 1;
      if (!(count > PRINT_SELECT_IMAGES_VALIDATION.max) && v.count) {
        v.count = count;
      }
    });
    setImages(data);
  }, [images, setImages])
  const handlerDecrementAll = useCallback(() => {
    const data = cloneDeep(images);
    data.forEach((v) => {
      const count = (v.count ?? 1) - 1;
      if (!(count < 1)) {
        v.count = count;
      }
    });
    setImages(data);
  }, [images, setImage]);

  const handlerAllRemove = useCallback(() => {
    switch(selectMode) {
      case 'image': {
        setImages((prev) => [...prev.map((v) => ({ ...v, count: 0 }))]);
        break;
      }
      case 'trimming': {
        setImages((prev) => [...prev.map((v) => {
          v.trimmingStatus = v.trimmingStatus === 3 ? 1 : v.trimmingStatus;
          return v;
        })]);
        break;
      }
      case 'date': {
        setImages((prev) => [...prev.map((v) => ({ ...v, datePrint: false }))]);
        break;
      }
    }
  }, [images, setImages])

  const selectOrder = useMemo(() => {
    const id = query.get('t') ?? '';
    const target = structure?.item?.[id];
    return target;
  }, [query, structure]);

  const validRotate = useMemo(() => {
    return (selectOrder?.templates?.length ?? 0) > 1;
  }, [selectOrder]);


  const handlerChangeSize = useCallback(() => {
    if (!selectOrder || !isInitializing) return;
    save(() => {
      dispatch(printActions.setIsNextRouteSelectImage(true));
      PrintSizeChange.category = selectOrder.productData?.serviceId;
      dispatch(push(RoutingPath.cnv.printOrderAddOrderSelect({ kijshopCd, shopOrderId })));
    });
  }, [
    dispatch,
    kijshopCd, shopOrderId,
    selectOrder,
    isInitializing,
    save,
  ]);

  const handlerAddOrder = useCallback(() => {
    if (!selectOrder || !isInitializing) return;
    save(() => {
      dispatch(printActions.setIsNextRouteSelectImage(true));
      dispatch(push(RoutingPath.cnv.printOrderAddOrderSelect({ kijshopCd, shopOrderId })));
    });
  }, [
    selectOrder,
    dispatch,
    isInitializing,
    kijshopCd, shopOrderId,
    save,
  ]);

  const handlerReturn = useCallback(() => {
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: [
        '編集を中断して注文概要画面に戻ります。',
        '保存されていない内容は破棄されます。よろしいですか？',
      ],
      buttons: [
        { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
        { label: 'OK', callback: () => {
          dispatch(dialogActions.pop());
          dispatch(push(RoutingPath.cnv.printOrder({ kijshopCd, shopOrderId })))
        }},
      ]
    }));
  }, [
    dispatch,
    kijshopCd, shopOrderId,
  ]);

  const handlerSave = useCallback((isComplete?: boolean) => {
    if (!isInitializing || !selectOrder) return;
    if (isComplete) {
      const invalid = structure.getInvalidCompleteItems(selectOrder.id, images);
      if (invalid.length) {
        dispatch(dialogActions.pushMessage({
          title: '警告',
          message: [
            'プリントする写真を選択してください',
            ...invalid.map((v) => `・${v.name}`),
          ],
          buttons: [
            { label: 'OK', callback: () => dispatch(dialogActions.pop()) },
          ],
        }));
        return;
      }
    }
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: [
        '編集内容を保存して完了します。',
        'よろしいですか？',
      ],
      buttons: [
        { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
        { label: 'OK', callback: () => {
          dispatch(dialogActions.popAll());
          save(() => {
            dispatch(push(RoutingPath.cnv.printOrder({ kijshopCd, shopOrderId })));
          }, isComplete);
        }},
      ],
    }));
  }, [
    dispatch,
    isInitializing,
    kijshopCd, shopOrderId,
    save,
    structure,
    images,
    selectOrder,
  ]);

  const getSaveThumbImages = useCallback(async(
    itemData: PrintItemDataEntity,
    imageDataList: PrintImagesDataEntity[],
  ) => {
    const result: ThumbImagesDataEntity[] = [];
    const getImage = (
      data: PrintImagesDataEntity,
      itemImageData: PrintProductImageDataEntity,
      retry = 0,
    ) => new Promise<ThumbImagesDataEntity>((resolve, reject) => {
      if (!itemImageData.thumbId) {
        resolve({ imageId: data.id });
        return;
      }
      if (retry > 2) reject();
      dispatch(apiActions.run(
        new ApiImagesGetOne({
          kijshopCd,
          path: `${data.path}/${data.filename}`,
        }),
        {
          onSuccess: (blob: Blob) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({ imageId: data.id, thumbId: itemImageData.thumbId, image: reader.result as string });
            }
            reader.readAsDataURL(blob);
          },
          onError: (blob: Blob) => {
            getImage(data, itemImageData, retry + 1)
              .then((res) => resolve(res))
              .catch(() => reject());
          },
        },
      ))
    });
    const images = itemData.images ?? [];
    for (const image of images) {
      const queryId = new URLSearchParams(`?${window.location.hash?.split('?')?.[1] ?? ''}`).get('t');
      const target = imageDataList.find((v) => v.id === image.thumbId && image.trimmingStatus === 2);
      if (!image.thumbId || !target) continue;
      if (itemData.id !== queryId) return;
      const resultData = await getImage(target, image);
      // console.warn(resultData);
      result.push(resultData);
      if (itemData.id !== queryId) return;
      setSaveThumbImages([...result]);
    }
  }, [
    dispatch,
    kijshopCd,
  ]);

  const initialize = useCallback(async(_id?: string) => {
    setIsInitializing(false);
    setUploadImageData([]);
    setSaveThumbImages([]);
    getMetaData();
    const _data = await getData();
    const data = _data.meta;
    setDatas(_data.original);
    setStructure(data);
    const id = _id || data.getItemList()?.[0].id;
    const itemData = data.item?.[id] ?? data.getItemList()?.[0];
    if (!itemData) {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: ['エラーが発生しました。'],
        buttons: [
          {
            label: '商品一覧へ戻る',
            callback: () => {
              dispatch(dialogActions.pop());
              dispatch(push(RoutingPath.cnv.printOrder({ kijshopCd, shopOrderId })));
            },
          },
        ],
      }));
      return;
    }

      if (leavePage.current) {
        return;
      }
      const kind = query.get('k') ?? '';
      if (kind) {
        switch(kind) {
          case 'i': {
            setSelectMode('image');
            break;
          }
          case 't': {
            if (itemData?.productData?.itemSetting?.trimming) {
              setSelectMode('trimming')
            }
            break;
          }
          case 'd': {
            if (itemData?.productData?.itemSetting?.printDate) {
              setSelectMode('date');
            }
            break;
          }
          default: break;
        }
      }
      dispatch(replace(
        `${RoutingPath.cnv.printOrderSelectImage({ kijshopCd, shopOrderId })}?t=${itemData.id}`,
      ));
      getThumbImages({ isPreview: true })
        .then((res) => {
          const imageList = res?.fileList ?? [];
          getSaveThumbImages(itemData, imageList)
          .catch(() => {
            dispatch(dialogActions.push({
              title: 'エラー',
              element: SeriousErrorDialog(),
            }));
          })
        }).catch(() => {
          dispatch(dialogActions.push({
            title: 'エラー',
            element: SeriousErrorDialog(),
          }));
        });
      getEditableImages(
        itemData,
        (ei, list) => {
          if (itemData) {
            createThumbImage(ei.id, {...itemData, images: list as unknown as PrintProductImageDataEntity[]}, list);
          }
        },
        setUploadImageData,
      ).then(() => {
        setIsInitializing(true);
      });
  }, [
    query,
    dispatch,
    kijshopCd, shopOrderId,
    getData,
    getMetaData,
    getEditableImages,
    createThumbImage,
    getSaveThumbImages,
    getThumbImages,
  ]);

  const handlerDelete = useCallback((id: string) => {
    if (structure.item?.[id] && structure.getItemList().length >= 2) {
      dispatch(dialogActions.pushMessage({
        title: '商品削除',
        message: [
          '選択した注文情報を削除します。',
          'よろしいですか？',
        ],
        buttons: [
          { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
          { label: 'OK', callback: () => {
            dispatch(dialogActions.pop());
            loading.setLoadingMessage('削除処理中です...');
              delete structure.item?.[id];
              dispatch(printActions.shopOrder({ kijshopCd, shopOrderId }).item().delete(id, {
                success: async(res) => {
                  if (res.getItemList().every((v) => v.status === PRINT_ITEM_STATUS.enableOrder)) {
                    await changeStatus('enableOrder');
                  } else {
                    await changeLastUpdateDate();
                  }
                  if (!selectItem) return;
                  if (selectItem.id === id) {
                    const target = structure.getItemList()?.[0];
                    if (!target) throw new Error('アイテムが存在しません');
                    await initialize(target.id);
                    loading.setLoadingMessage('');
                  } else {
                    if (selectItem && res.item?.[selectItem.id]) {
                      res.item[selectItem.id] = selectItem;
                      setStructure(res);
                      loading.setLoadingMessage('');
                    }
                  }
                }
              }));
            }},
          ]
        }))
    }
  }, [
    dispatch,
    kijshopCd, shopOrderId,
    structure,
    selectItem,
    initialize,
    loading,
    changeLastUpdateDate,
    changeStatus,
  ]);

  const onSelectTab = useCallback((id: string) => {
    if (id !== selectOrder?.id || !isInitializing) {
      save(() => {
        setSelectMode('image');
        initialize(id);
      });
    }
  }, [
    save,
    initialize,
    isInitializing,
    selectOrder,
  ]);

  const handlerImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpg, image/jpeg';
    input.multiple = true;
    input.onchange = async (event: any) => {
      const e = event as React.ChangeEvent<HTMLInputElement>;
      const uploadFiles = Array.from(e.target.files ?? []);
      const sequential = await filterUploadFiles(uploadFiles);
      if (!sequential.length) {
        return;
      }

      imageUpload(sequential);
    }
    input.click();
    input.remove();
  }, [
    imageUpload,
    filterUploadFiles,
  ]);
  const onChangeMode = useCallback((kind: Kind) => {
    setSelectMode(kind);
  }, []);

  const handlerChangeSort = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortValue(e.target.value);
  }, []);

  const handlerToggleViewSize = useCallback((flag: boolean) => {
    setViewLarge(flag);
  }, []);

  const handlerEditTrimming = useCallback((imageId: string) => {
    save(() => {
      if (!selectOrder) return;
      dispatch(push(`${RoutingPath.cnv.printOrderTrimming({
        kijshopCd,
        shopOrderId,
        orderId: selectOrder.id,
        imageId,
      })}?k=${KIND_QUERY[selectMode]}`));
    });
  }, [
    save,
    selectOrder,
    kijshopCd, shopOrderId,
    dispatch,
    selectMode,
  ]);

  const handlerRotate = useCallback(async(imageId: string) => {
    if (!selectOrder) throw new Error('商品が選択されていません');
    if (!validRotate) return;
    const clone = cloneDeep(images);
    const target = clone.find((v) => v.id === imageId);
    if (!target) return;
    const check = () => new Promise<void>((resolve) => {
      if (
        !resetThumbList.find((v) => v === imageId)
        && datas.find((v) => v.filename === `${selectItem?.id ?? ''}_${imageId}`)
        && target.thumbId
        && target.trimmingStatus === 2
      ) {
        dispatch(dialogActions.pushMessage({
          title: '確認',
          message: [
            'トリミング編集画面での操作が破棄されます。',
            '実行してよろしいですか？'
          ],
          buttons: [
            { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
            { label: 'OK', callback: () => {
              dispatch(dialogActions.pop());
              resolve();
            }},
          ]
        }))
      } else {
        resolve();
      }
    })
    await check();
    target.rotate = !Number(target.rotate) ? '1' : '0';
    target.trimmingStatus = target.trimmingStatus !== 3 ? 1 : target.trimmingStatus;
    target.thumbId = undefined;
    setImages(clone);
    window.setTimeout(() => {
      createThumbImage(
        imageId,
        {...selectOrder, images: clone as any as PrintProductImageDataEntity[]},
        undefined,
        validRotate,
        undefined,
        Number(target.rotate) ? '2' : '1',
      )
        .then(() => {
          setResetThumbList((prev) => {
            const target = prev.find((v) => v === imageId);
            if (!target) prev.push(imageId);
            return [...prev];
          });
        }
      );
    })
  }, [
    dispatch,
    selectItem,
    createThumbImage,
    selectOrder,
    validRotate,
    setImages,
    images,
    resetThumbList,
    datas,
  ]);

  const handlerClickDatePrintUsage = useCallback(() => {
    NewPage.tab(webPagePath.datePrintUsage);
  }, []);

  const isTrimmed = useCallback((imageId: string) => {
    // console.log('select: ', selectItem);
    if (!selectItem) return false;
    // console.log(datas);
    const targetData = datas.find((v) => v.filename === `${selectItem?.id}_${imageId}`);
    // targetData && console.log('target: ', targetData);
    if (!targetData) return false;
    const targetImage = images.find((v) => v.id === imageId);
    // targetImage && console.log(targetImage);
    return !!targetImage?.thumbId && (targetImage.trimmingStatus === 2);
  }, [
    selectItem,
    datas,
    images,
  ]);

  const loadImage = useMemo(() => {
    if (!selectItem) return false;
    if (uploadProgress?.queue) {
      return false;
    }
    const result = uploadImageData.every((v) => {
      const target = viewImages.find((v2) => v2.id === v.id);
      if (!target) return false;
      if (target.thumbId && target.trimmingStatus === 2) {
        return !!saveThumbImages.find((v2) => target.thumbId === v2.thumbId)?.image;
      }
      return !!viewImages.find((v2) => v2.id === target.id)?.thumbBase64;
    });
    return result;
  }, [
    uploadProgress,
    viewImages,
    saveThumbImages,
    selectItem,
    uploadImageData,
  ]);
  useEffect(() => {
    const id = query.get('t') ?? '';
    initialize(id);
    dispatch(commonActions.getData(kijshopCd, { version: true, masterShop: true }));
    return(() => {
      leavePage.current = true;
    })
  }, []);

  return {
    structure,
    updateStructure,
    images: viewImages,
    validRotate,
    buttons: {
      image: {
        select: selectMode === 'image',
        onClick: () => onChangeMode('image'),
      },
      trimming: {
        select: selectMode === 'trimming',
        onClick: () => onChangeMode('trimming'),
        disabled: !selectItem?.productData?.itemSetting?.trimming,
      },
      date: {
        select: selectMode === 'date',
        onClick: () => onChangeMode('date'),
        disabled: !selectItem?.productData?.itemSetting?.printDate,
      },
    },
    sort: {
      value: sortValue,
      list: printSelectImageSort,
      onChange: handlerChangeSort,
    },
    align: {
      value: viewLarge,
      onClick: handlerToggleViewSize,
    },
    onSelectImage,
    setSelectImageCount,
    handlerChangeSize,
    selectOrder,
    onSelectTab,
    themeClassName,
    footerCollapseState: footerState,
    handlerReturn,
    handlerSave,
    handlerAddOrder,
    handlerDelete,
    loading,
    isInitializing,
    handlerImageUpload,
    handlerAllSelect,
    handlerAllRemove,
    handlerIncrementAll,
    handlerDecrementAll,
    handlerEditTrimming,
    handlerRotate,
    handlerClickDatePrintUsage,
    getRotate,
    thumbImages,
    isTrimmed,
    saveThumbImages,
    loadImage,
  }
}
